// services/guardianService.js - Guardian student management
import { get, post, put, del } from '@aws-amplify/api-rest';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'CourseBookingApi';

// Helper to get auth headers
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens || !session.tokens.idToken) {
      console.error('Authentication Failure: No valid session or token found.');
      throw new Error('No authentication token');
    }
    const headers = {
      Authorization: `Bearer ${session.tokens.idToken.toString()}`
    };
    //console.log('Authorization Headers:', headers);
    return headers;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
}

// Enhanced error handling wrapper (same pattern as studentService)
const handleApiCall = async (apiCall) => {
  try {
    let response = await apiCall();

    // UNWRAP {response: Promise, ...}
    if (response && response.response && typeof response.response.then === 'function') {
      response = await response.response;
    }

    // Parse fetch Response
    if (response && typeof response.json === 'function') {
      response = await response.json();
    }

    // Handle ReadableStream (Amplify/fetch)
    if (response && response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader();
      let chunks = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) chunks.push(...value);
      }
      const text = new TextDecoder().decode(new Uint8Array(chunks));
      try {
        response = { ...response, ...JSON.parse(text) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    // If .body is a string, parse it and merge (the classic Lambda proxy pattern)
    if (response && typeof response.body === 'string') {
      try {
        response = { ...response, ...JSON.parse(response.body) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    return response;
  } catch (error) {
    console.error(`API Error in ${apiCall.name}:`, error);

    // Check if it's an auth error
    if (error.statusCode === 401 || error.name === 'NotAuthorizedException') {
      console.error('Authentication error - user may need to sign in again');
    }

    throw error;
  }
};

// Guardian Student Management
export const guardianService = {
  // Add a student to guardian's account
  async addStudent(studentData) {
    return handleApiCall(async function addStudent() {
      console.log('Adding student:', studentData);
      
      // Map frontend field names to backend expected field names
      const backendData = {
        studentEmail: studentData.email,
        studentName: studentData.name,
        relationship: studentData.relationship,
        gender: studentData.gender,
        extendedProfile: studentData.extendedProfile
      };
      
      return await post({
        apiName: API_NAME,
        path: 'guardians/students',
        options: {
          body: backendData,
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Get all students for the guardian
  async getStudents() {
    return handleApiCall(async function getStudents() {
      console.log('Getting guardian students...');
      
      return await get({
        apiName: API_NAME,
        path: 'guardians/students',
        options: {
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Check if student can be deleted (no active bookings)
  async checkStudentCanDelete(studentEmail) {
    return handleApiCall(async function checkStudentCanDelete() {
      console.log('Checking if student can be deleted:', studentEmail);
      
      return await get({
        apiName: API_NAME,
        path: `guardians/students/${encodeURIComponent(studentEmail)}/can-delete`,
        options: {
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Remove a student from guardian's account
  async removeStudent(studentEmail) {
    return handleApiCall(async function removeStudent() {
      console.log('Removing student:', studentEmail);
      
      return await del({
        apiName: API_NAME,
        path: `guardians/students/${encodeURIComponent(studentEmail)}`,
        options: {
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Update a student's information
  async updateStudent(studentEmail, updates) {
    return handleApiCall(async function updateStudent() {
      console.log('Updating student:', studentEmail, updates);
      
      return await put({
        apiName: API_NAME,
        path: `guardians/students/${encodeURIComponent(studentEmail)}`,
        options: {
          body: {
            studentName: updates.name,
            gender: updates.gender,
            relationship: updates.relationship
          },
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Update extended student profile
  async updateStudentProfile(studentEmail, profileData) {
    return handleApiCall(async function updateStudentProfile() {
      //console.log('🔍 [DEBUG] guardianService.updateStudentProfile called with:', studentEmail, profileData);
      
      try {
        const headers = await getAuthHeaders();
        //console.log('🔍 [DEBUG] Got auth headers:', headers);
        
        const path = `guardians/students/${encodeURIComponent(studentEmail)}/profile`;
        //console.log('🔍 [DEBUG] API path:', path);
        
        const result = await put({
          apiName: API_NAME,
          path: path,
          options: {
            body: profileData,
            headers: headers
          }
        });
        
        //console.log('🔍 [DEBUG] API call result:', result);
        return result;
      } catch (error) {
        console.error('🔍 [DEBUG] Error in updateStudentProfile:', error);
        throw error;
      }
    });
  },

  // Get bookings for a specific student
  async getStudentBookings(studentEmail) {
    return handleApiCall(async function getStudentBookings() {
      console.log('🔎 [guardianService.getStudentBookings] Getting bookings for:', studentEmail);
      
      const result = await get({
        apiName: API_NAME,
        path: `guardians/students/${encodeURIComponent(studentEmail)}/bookings`,
        options: {
          headers: await getAuthHeaders()
        }
      });
      
      //console.log('📥 [guardianService.getStudentBookings] Raw API response:', result);
      
      // The backend returns { studentEmail, bookings, count }
      // But we need to ensure bookings is accessible
      if (result && !result.bookings && result.body) {
        console.log('🔄 [guardianService.getStudentBookings] Parsing body:', result.body);
        try {
          const parsed = typeof result.body === 'string' ? JSON.parse(result.body) : result.body;
          return parsed;
        } catch (e) {
          console.error('❌ [guardianService.getStudentBookings] Failed to parse body:', e);
        }
      }
      
      return result;
    });
  },

  // Create booking for a student
  async createBookingForStudent(bookingData) {
    return handleApiCall(async function createBookingForStudent() {
      console.log('Creating booking for student:', bookingData);
      
      // The bookingData should include studentEmail field
      return await post({
        apiName: API_NAME,
        path: 'bookings',
        options: {
          body: bookingData,
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Get detailed student profile for editing
  async getStudentDetails(studentEmail) {
    return handleApiCall(async function getStudentDetails() {
      console.log('Getting detailed student profile for:', studentEmail);
      
      return await get({
        apiName: API_NAME,
        path: `guardians/students/${encodeURIComponent(studentEmail)}/details`,
        options: {
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Get all bookings for all students under guardian
  async getAllStudentBookings() {
    try {
      console.log('🚀 [guardianService] Starting getAllStudentBookings...');
      
      // First get all students
      const studentsResponse = await this.getStudents();
      console.log('📋 [guardianService] Students response:', studentsResponse);
      
      const students = studentsResponse.students || [];
      console.log(`👥 [guardianService] Found ${students.length} total students`);
      
      // DEBUG: If no students found, this is the core issue
      if (students.length === 0) {
        console.error('❌ [guardianService] CRITICAL: No students found for parent!');
        console.error('❌ [guardianService] This means parent-student relationship is NOT set up');
        console.error('❌ [guardianService] Check GuardianStudentTable for relationships');
        console.error('❌ [guardianService] Parent must add students to their account first');
        return { students: [], bookings: [] };
      }
      
      // Then get bookings for each student (filter out students with undefined emails)
      const validStudents = students.filter(student => 
        student.email && 
        student.email !== 'undefined' && 
        student.email !== null && 
        student.email.trim() !== ''
      );
      console.log('🔍 [guardianService] All students from backend:', students);
      console.log('🔍 [guardianService] Valid students for booking fetch:', validStudents);
      
      // Debug invalid students
      const invalidStudents = students.filter(student => 
        !student.email || 
        student.email === 'undefined' || 
        student.email === null || 
        student.email.trim() === ''
      );
      if (invalidStudents.length > 0) {
        console.error('❌ [guardianService] Invalid students found:', invalidStudents);
      }
      
      const bookingsPromises = validStudents.map(student => {
        console.log(`📚 [guardianService] Fetching bookings for student: ${student.email}`);
        
        return this.getStudentBookings(student.email)
          .then(result => {
            console.log(`✅ [guardianService] Bookings response for ${student.email}:`, result);
            return result;
          })
          .catch(err => {
            console.error(`❌ [guardianService] Error fetching bookings for ${student.email}:`, err);
            
            // Handle authorization errors for newly added students gracefully
            if (err.message && err.message.includes('not authorized')) {
              console.log(`⚠️ [guardianService] Skipping bookings for newly added student: ${student.email}`);
              return {
                studentEmail: student.email,
                bookings: [],
                error: null // Don't treat this as an error
              };
            }
            return {
              studentEmail: student.email,
              bookings: [],
              error: err.message
            };
          });
      });
      
      const bookingsResults = await Promise.all(bookingsPromises);
      console.log('📊 [guardianService] All booking results:', bookingsResults);
      
      // Combine all bookings
      const allBookings = [];
      bookingsResults.forEach((result, index) => {
        console.log(`🔄 [guardianService] Processing result ${index + 1}:`, result);
        
        if (result && result.bookings && Array.isArray(result.bookings)) {
          console.log(`  📌 Found ${result.bookings.length} bookings`);
          result.bookings.forEach(booking => {
            const enrichedBooking = {
              ...booking,
              studentEmail: result.studentEmail || booking.studentEmail,
              studentName: students.find(s => s.email === (result.studentEmail || booking.studentEmail))?.name
            };
            allBookings.push(enrichedBooking);
          });
        } else if (result && result.Items) {
          // Handle if backend returns Items instead of bookings
          console.log(`  📌 Found ${result.Items.length} Items`);
          result.Items.forEach(booking => {
            const enrichedBooking = {
              ...booking,
              studentEmail: result.studentEmail || booking.studentEmail,
              studentName: students.find(s => s.email === (result.studentEmail || booking.studentEmail))?.name
            };
            allBookings.push(enrichedBooking);
          });
        } else {
          console.log('  ⚠️ No bookings found in result');
        }
      });
      
      console.log(`🎯 [guardianService] Total bookings found: ${allBookings.length}`);
      console.log('📦 [guardianService] All bookings:', allBookings);
      
      // Get waitlist entries for all students
      let allWaitlistEntries = [];
      try {
        console.log('🔍 [guardianService] Fetching waitlist entries...');
        const waitlistResponse = await get({
          apiName: API_NAME,
          path: 'waitlist/entries',
          options: {
            headers: await getAuthHeaders()
          }
        });
        
        const waitlistData = await waitlistResponse.response;
        if (waitlistData && waitlistData.body) {
          // Check if body is already parsed or needs parsing
          let parsedData;
          if (typeof waitlistData.body === 'string') {
            try {
              parsedData = JSON.parse(waitlistData.body);
            } catch (parseError) {
              console.error('Failed to parse waitlist response body:', parseError);
              parsedData = { entries: [] };
            }
          } else {
            // Body is already an object
            parsedData = waitlistData.body;
          }
          allWaitlistEntries = parsedData.entries || [];
          console.log(`📋 [guardianService] Found ${allWaitlistEntries.length} waitlist entries`);
        }
      } catch (err) {
        console.error('Error fetching waitlist entries:', err);
      }
      
      return {
        students: validStudents, // Return only valid students
        bookings: allBookings,
        totalBookings: allBookings.length,
        waitlistEntries: allWaitlistEntries
      };
    } catch (error) {
      console.error('Error fetching all student bookings:', error);
      throw error;
    }
  },

  // Get guardian's own profile
  async getGuardianProfile() {
    return handleApiCall(async function getGuardianProfile() {
      console.log('Getting guardian profile...');

      return await get({
        apiName: API_NAME,
        path: 'guardians/profile',
        options: {
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Get guardian profile with billing data from bookings (pre-populated)
  async getGuardianProfileWithBookingData() {
    return handleApiCall(async function getGuardianProfileWithBookingData() {
      console.log('Getting guardian profile with booking data...');

      return await get({
        apiName: API_NAME,
        path: 'guardians/profile/with-booking-data',
        options: {
          headers: await getAuthHeaders()
        }
      });
    });
  },

  // Update guardian's own profile
  async updateGuardianProfile(profileData) {
    return handleApiCall(async function updateGuardianProfile() {
      console.log('Updating guardian profile:', profileData);

      return await put({
        apiName: API_NAME,
        path: 'guardians/profile',
        options: {
          body: profileData,
          headers: await getAuthHeaders()
        }
      });
    });
  }
};

export default guardianService;